import { Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Loader2 } from "lucide-react";
import { TrialBanner } from "@/components/trial/TrialBanner";
import { useTrialPageTracking } from "@/hooks/useTrialPageTracking";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireSuperAdmin?: boolean;
  /** Minimum role required to access this route (e.g., "admin") */
  requiredRole?: "admin" | "member" | "user";
}

/** Role hierarchy: admin > member > user */
const ROLE_HIERARCHY: Record<string, number> = {
  admin: 3,
  member: 2,
  user: 1,
};

/**
 * ProtectedRoute component that checks authentication status before rendering children.
 * Shows loading spinner while checking auth, redirects to login if not authenticated,
 * checks for trial expiration, and optionally checks for super-admin or role-based access.
 *
 * It also owns the two pieces of authenticated chrome that used to live in the
 * classic `AppLayout` (PLAT-466). That shell ended up rendering only on the
 * NotFound page after the 2026-07-20 cutover, which meant the trial countdown
 * and its "schedule a demo" CTA appeared **only on 404** — a trial user on a
 * real page saw nothing, and fired no trial page-view analytics.
 *
 * They belong here rather than in `TaskShellPage`, which only 6 pages use: this
 * component wraps every authenticated route, and it already owns trial state
 * (it does the expired-trial redirect below). `TrialBanner` self-suppresses for
 * non-trial, expired, and dismissed users, so non-trial users render nothing.
 */
export function ProtectedRoute({
  children,
  requireSuperAdmin = false,
  requiredRole,
}: ProtectedRouteProps) {
  const { user, loading, isSuperuser, userProfile, isTrialExpired } = useAuth();

  useTrialPageTracking();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Redirect expired trial users to the trial-expired page
  if (isTrialExpired) {
    return <Navigate to="/trial-expired" replace />;
  }

  if (requireSuperAdmin && !isSuperuser) {
    return <Navigate to="/rune" replace />;
  }

  // Role-based access check
  if (requiredRole) {
    const userRole = userProfile?.roleType || "user";
    const userLevel = ROLE_HIERARCHY[userRole] || 0;
    const requiredLevel = ROLE_HIERARCHY[requiredRole] || 0;

    if (userLevel < requiredLevel) {
      return <Navigate to="/rune" replace />;
    }
  }

  return (
    <>
      <TrialBanner />
      {children}
    </>
  );
}
